import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable } from "mobx";
import { fromPromise } from "mobx-utils";
import { AdminNotificationsDataLayerSymbol } from "./AdminNotificationsDataLayerSymbol";
import type { IAdminNotificationsDataLayer } from "../types/IAdminNotificationsDataLayer";
import type { TFunction } from "i18next";

@injectable()
export class AdminNotificationsStore {
  fullOptions: { config: string; val: boolean; label: string }[] | undefined = undefined;
  emails: string[] = [];
  recipients: Record<string, string[]> = {};
  private loadedEmailsByEvent: Record<string, string[]> = {};
  isSaving = false;
  initialized = false;
  t: TFunction<"translation", undefined> = (() => "") as any;

  constructor(@inject(AdminNotificationsDataLayerSymbol) private adminNotificationsDataLayer: IAdminNotificationsDataLayer) {
    makeObservable(this, {
      emails: observable,
      recipients: observable,
      setRecipients: action.bound,
      fullOptions: observable,
      initialized: observable,
      init: computed,
      save: action.bound,
      onSwitchChange: action.bound,
      isSaving: observable,
      setProps: action,
      options: computed,
      groupSyncOptions: computed,
      isReadonly: computed,
      t: observable.ref,
    });
  }

  get isReadonly() {
    return Boolean(this.adminNotificationsDataLayer.isReadonly);
  }

  setProps(t: TFunction) {
    this.t = t;
    this.initialized = true;
  }

  get options() {
    return [
      {
        config: "USER_LOGGED_OUT",
        label: this.t("adminNotifications.user_logged_out"),
        val: false,
      },
      {
        config: "USER_LOGGED_IN",
        label: this.t("adminNotifications.user_logged_in"),
        val: false,
      },
      {
        config: "USER_UNINSTALLED_EXTENSION",
        label: this.t("adminNotifications.user_uninstall_extension"),
        val: false,
      },
      {
        config: "API_CURRENTLY_DISABLED",
        label: this.t("adminNotifications.api_currently_disabled"),
        val: false,
      },
      {
        config: "APEX_REST_SERVICES_DISABLED",
        label: this.t("adminNotifications.apex_rest_services_disabled"),
        val: false,
      },
      {
        config: "NEW_USER_CREATED",
        label: this.t("adminNotifications.new_user_created"),
        val: false,
      },
      {
        config: "USER_TURNED_AUTO_SYNC_OFF",
        label: this.t("adminNotifications.user_turned_auto_sync_off"),
        val: false,
      },
      {
        config: "USER_TURNED_AUTO_SYNC_ON",
        label: this.t("adminNotifications.user_turned_auto_sync_on"),
        val: false,
      },
      {
        config: "MONTHLY_USER_VERSIONS_REPORT",
        label: this.t("adminNotifications.monthly_user_versions_report"),
        val: false,
      },
    ];
  }

  get groupSyncOptions() {
    return [
      {
        config: "USER_TURNED_GROUP_AUTO_SYNC_OFF",
        label: this.t("adminNotifications.user_turned_group_auto_sync_off"),
        val: false,
      },
      {
        config: "USER_TURNED_GROUP_AUTO_SYNC_ON",
        label: this.t("adminNotifications.user_turned_group_auto_sync_on"),
        val: false,
      },
    ];
  }

  get init() {
    if (!this.initialized) return fromPromise.reject("not initialized");
    return fromPromise(this.initData(this.options, this.groupSyncOptions));
  }

  private async initData(
    options: { config: string; label: string; val: boolean }[],
    groupSyncOptions: { config: string; label: string; val: boolean }[]
  ) {
    const { flags, emails, emailsByEvent, allowSyncGroups } = await this.adminNotificationsDataLayer.init();

    this.fullOptions = (allowSyncGroups ? [...options, ...groupSyncOptions] : [...options]).map((option) => {
      if (flags.find((flag) => flag === option.config)) {
        option.val = true;
        return option;
      }
      option.val = false;
      return option;
    });
    this.emails = emails.filter(Boolean);
    this.loadedEmailsByEvent = emailsByEvent ?? {};
    // Orgs saved before per-event lists existed start the enabled events with the shared list;
    // disabled events start empty so turning one on doesn't inherit everyone
    const legacyEmailsFor = (option: { val: boolean }) => (option.val ? this.emails : []);
    this.recipients = Object.fromEntries(
      this.fullOptions.map((option) => [
        option.config,
        (emailsByEvent ? (emailsByEvent[option.config] ?? []) : legacyEmailsFor(option)).filter(Boolean),
      ])
    );
  }

  setRecipients(config: string, emails: string[]) {
    this.recipients = { ...this.recipients, [config]: emails };
  }

  onSwitchChange(val: boolean, label: string) {
    this.fullOptions = this.fullOptions?.map((value) => {
      if (value.label === label) {
        value.val = val;
      }
      return value;
    });
  }

  setIsSaving(val: boolean) {
    this.isSaving = val;
  }

  async save() {
    const flags = this.fullOptions?.reduce((res, option) => {
      if (option.val) res.push(option.config);
      return res;
    }, [] as string[]);
    // Keep the lists of events this page doesn't show (e.g. group sync while it's disabled); the server replaces the whole map
    const emailsByEvent = {
      ...this.loadedEmailsByEvent,
      ...Object.fromEntries((this.fullOptions ?? []).map((option) => [option.config, this.recipients[option.config] ?? []])),
    };
    const emails = [...new Set(Object.values(emailsByEvent).flat().filter(Boolean))];
    this.setIsSaving(true);
    const res = await this.adminNotificationsDataLayer.save({ flags, emails, emailsByEvent });
    this.setIsSaving(false);
    return res;
  }
}
